/**
 * Is a printed name really the species name, misread?
 *
 * Small and dependency-free on purpose: the OCR reader (myPokemonOcr) and the
 * stored-record sanitiser (myPokemonSanitize) both need it, and the sanitiser
 * must not pull the OCR chunk into the main bundle.
 *
 * Two device cases (2026-09-17): Vision read "Clamperl" as "Clamperı" (a
 * dotless i, one edit away) and a mid-render frame gave "Spriga:" for
 * "Sprigatito" (a prefix). Both were stored as nicknames and the HUD then
 * printed "Spriga: · SPRIGATITO", the double name the owner asked to see on
 * no view at all.
 */

/** Uppercase alphanumerics, accents folded, gender glyphs as letters. */
export function normalizeSpeciesToken(s: string): string {
  return (s || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toUpperCase()
    .replace(/♀/g, "F")
    .replace(/♂/g, "M")
    .replace(/[^A-Z0-9]/g, "");
}

/** True when `a` and `b` (5+ chars) differ by one substitution, insertion or deletion. */
export function withinOneEdit(a: string, b: string): boolean {
  if (a === b) return false;
  if (Math.min(a.length, b.length) < 5) return false;
  if (Math.abs(a.length - b.length) > 1) return false;
  let i = 0;
  while (i < a.length && i < b.length && a[i] === b[i]) i++;
  const ta = a.slice(i);
  const tb = b.slice(i);
  if (a.length === b.length) return ta.slice(1) === tb.slice(1);
  return a.length > b.length ? ta.slice(1) === tb : ta === tb.slice(1);
}

/** Shortest prefix of a species name accepted as a truncated read of it. */
const MIN_PREFIX = 4;

/**
 * True when `name` is the species name misread: the same token, one edit
 * away on five or more letters, or a truncated prefix of four or more
 * letters ("SPRIGA" for "SPRIGATITO"; "PICHU" is not a prefix of "PIKACHU").
 */
export function looksLikeSpeciesMisread(
  name: string | null | undefined,
  speciesName: string | null | undefined,
): boolean {
  if (!name || !speciesName) return false;
  const a = normalizeSpeciesToken(name);
  const b = normalizeSpeciesToken(speciesName);
  if (a.length === 0 || b.length === 0) return false;
  if (a === b) return true;
  if (withinOneEdit(a, b)) return true;
  return a.length >= MIN_PREFIX && a.length < b.length && b.startsWith(a);
}
